/* =========================================================================
 * gift-with-code.js
 *
 * Three trigger modes (configured in theme settings, exposed via window.FoxGiftCode.mode):
 *   - "code"            : customer types a magic code into the cart discount input
 *   - "minimum_amount"  : cart subtotal (excluding the gift line) reaches a threshold
 *   - "both"            : either of the above paths can fire
 *
 * The script is idempotent: it runs on load, after every cart mutation, and on
 * theme cart events. It will never add more than one gift line (matched by
 * variant id + properties._gift).
 * ===================================================================== */

(function () {
  'use strict';

  var cfg = window.FoxGiftCode;
  if (!cfg || !cfg.variantId) return;

  var MODE                  = cfg.mode || 'code';
  var TRIGGER               = (cfg.trigger || '').trim().toLowerCase();
  var VARIANT_ID            = Number(cfg.variantId);
  var REQUIRE_ITEMS         = !!cfg.requireItems;
  var AUTO_REMOVE_EMPTY     = !!cfg.autoRemove;
  var AUTO_REMOVE_BELOW_MIN = !!cfg.autoRemoveBelowMinimum;
  var MIN_CENTS             = Number(cfg.minimumCents) || 0;
  var ROUTES                = cfg.routes || {};
  var MESSAGES              = cfg.messages || {};

  var CODE_ACTIVE = (MODE === 'code' || MODE === 'both') && TRIGGER.length > 0;
  var MIN_ACTIVE  = (MODE === 'minimum_amount' || MODE === 'both') && MIN_CENTS > 0;

  // ----- internal state to prevent overlapping cart mutations ------------
  var inFlight = false;
  var pending  = false;

  // =====================================================================
  // Cart helpers
  // =====================================================================
  function getCart() {
    return fetch(ROUTES.cartJson || '/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
    }).then(function (r) { return r.json(); });
  }

  function findGiftLine(cart) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (Number(it.variant_id) === VARIANT_ID &&
          it.properties && String(it.properties._gift) === 'true') {
        return it;
      }
    }
    return null;
  }

  function hasNonGiftItems(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(function (it) {
      return !(it.properties && String(it.properties._gift) === 'true');
    });
  }

  function subtotalExcludingGift(cart) {
    if (!cart) return 0;
    var subtotal = cart.items_subtotal_price != null
      ? cart.items_subtotal_price
      : cart.total_price;
    var gift = findGiftLine(cart);
    if (gift) subtotal -= gift.original_line_price;
    return subtotal;
  }

  function addGift() {
    return fetch(ROUTES.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{
          id: VARIANT_ID,
          quantity: 1,
          properties: { _gift: 'true' }
        }]
      })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart/add failed: ' + r.status);
      return r.json();
    });
  }

  function removeLine(line) {
    // /cart/change.js with id = line.key removes the specific line.
    return fetch(ROUTES.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: line.key, quantity: 0 })
    }).then(function (r) { return r.json(); });
  }

  // Try to apply the typed code as a real Shopify discount, in addition to
  // adding the gift. If the merchant has set up a Shopify discount with the
  // same code, this will activate it; if not, Shopify silently ignores it
  // and only the gift remains. Either way the promise resolves — we never
  // surface a failure here, because the gift was already added successfully.
  function applyShopifyDiscount(code) {
    if (!code) return Promise.resolve(null);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: code })
    }).then(function (r) {
      // 200/422 both possible; we don't care — the cart endpoint will
      // reflect whatever Shopify decided to do with the code.
      return r.json().catch(function () { return null; });
    }).catch(function () { return null; });
  }

  // =====================================================================
  // Theme integration — refresh cart UI after we mutate the cart
  // =====================================================================
  function refreshCartUI() {
    // Fire a few common events so the theme's cart drawer re-renders.
    var evts = ['cart:refresh', 'cart:updated', 'cart:change'];
    evts.forEach(function (name) {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
    });
    // Some FoxEcom-based themes use a global cart store; nudge it if present.
    if (window.theme && typeof window.theme.cartUpdate === 'function') {
      try { window.theme.cartUpdate(); } catch (e) {}
    }
  }

  // =====================================================================
  // Core evaluation — decide whether the gift should be added or removed
  // =====================================================================
  function evaluate() {
    if (inFlight) { pending = true; return Promise.resolve(); }
    inFlight = true;

    return getCart().then(function (cart) {
      var giftLine        = findGiftLine(cart);
      var hasItems        = hasNonGiftItems(cart);
      var subtotalExGift  = subtotalExcludingGift(cart);
      var minReached      = MIN_ACTIVE && subtotalExGift >= MIN_CENTS;

      // --- 1. Auto-remove if cart has no other items ----------------------
      if (giftLine && AUTO_REMOVE_EMPTY && !hasItems) {
        return removeLine(giftLine).then(refreshCartUI);
      }

      // --- 2. Minimum-amount path ----------------------------------------
      if (MIN_ACTIVE) {
        // 2a. Threshold reached → add gift if not already there
        if (!giftLine && minReached && (!REQUIRE_ITEMS || hasItems)) {
          return addGift().then(refreshCartUI).catch(function () {});
        }
        // 2b. Dropped below threshold → remove the gift (only if added by this rule)
        //    Note: if mode is "both" and the customer earned the gift via the code,
        //    we still remove it once they fall below — this is the documented behavior.
        if (giftLine && !minReached && AUTO_REMOVE_BELOW_MIN) {
          return removeLine(giftLine).then(refreshCartUI);
        }
      }
      // No-op
    }).catch(function (err) {
      console.warn('[gift-with-code] evaluate failed', err);
    }).then(function () {
      inFlight = false;
      if (pending) { pending = false; evaluate(); }
    });
  }

  // =====================================================================
  // Code interception — intercept the cart discount form submit
  // =====================================================================
  function showFormMessage(form, msg, type) {
    if (!form || !msg) return;
    var box = form.querySelector('.form__message');
    if (!box) return;
    box.textContent = msg;
    box.classList.remove('alert--success', 'alert--error', 'alert--info');
    if (type === 'success') box.classList.add('alert--success');
    else if (type === 'info') box.classList.add('alert--info');
    else box.classList.add('alert--error');
    box.hidden = false;
  }

  function attachCodeInterceptor() {
    if (!CODE_ACTIVE) return;

    // Use capture phase so we run before the theme's cart-discount handler.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLElement)) return;

      // Match the cart discount form. The theme uses <form is="cart-discount">.
      var isCartDiscountForm =
        form.matches('form[is="cart-discount"]') ||
        form.classList.contains('cart-discount__form') ||
        (form.querySelector && form.querySelector('input[name="discount"]') &&
         (form.action || '').indexOf('/cart') !== -1);

      if (!isCartDiscountForm) return;

      var input = form.querySelector('input[name="discount"]');
      if (!input) return;

      var entered = (input.value || '').trim().toLowerCase();
      if (entered !== TRIGGER) return; // let real discount codes through

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      getCart().then(function (cart) {
        var giftLine = findGiftLine(cart);
        var hasItems = hasNonGiftItems(cart);

        if (REQUIRE_ITEMS && !hasItems) {
          showFormMessage(form, MESSAGES.emptyCart, 'error');
          return;
        }

        // Whether or not the gift is already there, we still want to push the
        // code to Shopify — the customer may be re-typing it to also activate
        // a real discount that didn't exist when the gift was first added.
        var giftStep = giftLine
          ? Promise.resolve(null)              // gift already in cart, skip add
          : addGift();

        return giftStep
          .then(function () { return applyShopifyDiscount(TRIGGER); })
          .then(function () {
            if (giftLine) {
              showFormMessage(form, MESSAGES.alreadyAdded, 'info');
            } else {
              showFormMessage(form, MESSAGES.success, 'success');
            }
            input.value = '';
            refreshCartUI();
          });
      }).catch(function () {
        showFormMessage(form, MESSAGES.error, 'error');
      });
    }, true);
  }

  // =====================================================================
  // React to cart changes — wrap fetch + listen to theme events
  // =====================================================================
  function isCartMutationUrl(url) {
    if (!url) return false;
    var s = String(url);
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(s);
  }

  function installFetchHook() {
    if (!window.fetch) return;
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url);
      var p = origFetch.apply(this, arguments);
      if (isCartMutationUrl(url)) {
        // Only react to non-gift-with-code calls — schedule re-eval after the response.
        p.then(function () { setTimeout(evaluate, 50); }, function () {});
      }
      return p;
    };
  }

  function installXhrHook() {
    if (!window.XMLHttpRequest) return;
    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__gwcUrl = url;
      return open.apply(this, arguments);
    };
    var send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      var self = this;
      this.addEventListener('load', function () {
        if (isCartMutationUrl(self.__gwcUrl)) setTimeout(evaluate, 50);
      });
      return send.apply(this, arguments);
    };
  }

  // =====================================================================
  // Boot
  // =====================================================================
  function boot() {
    attachCodeInterceptor();
    installFetchHook();
    installXhrHook();

    ['cart:updated', 'cart:refresh', 'cart:change'].forEach(function (name) {
      document.addEventListener(name, function () { setTimeout(evaluate, 0); });
    });

    // Initial evaluation — covers the case where the customer lands on the
    // cart page already over the minimum (e.g. came back from a saved cart).
    if (MIN_ACTIVE || AUTO_REMOVE_EMPTY) evaluate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();